class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.grid = null;
    this.container = null;
    this.button = null;
    this.progressBar = null;
    this.progressCurrent = null;
    this.totalProducts = 0;
    this.nextUrl = null;
    this.autoLoad = false;
    this.loading = false;
    this.controller = new AbortController();
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-product-grid]');
    this.container = this.querySelector('[data-load-more-container]');
    this.button = this.querySelector('[data-load-more-button]');
    this.progressBar = this.querySelector('[data-progress-bar]');
    this.progressCurrent = this.querySelector('[data-progress-current]');
    this.totalProducts = parseInt(this.getAttribute('data-total-products'), 10);
    this.nextUrl = this.getAttribute('data-next-url');
    this.autoLoad = this.getAttribute('data-auto-load') === 'true';
    this.loading = false;

    if (this.button) {
      this.button.addEventListener('click', () => this.loadMore(), {
        signal: this.controller.signal,
      });
    }

    // If landed on page > 1, load previous pages first
    const currentPage = parseInt(this.getAttribute('data-current-page')) || 1;
    const loadPrevious = this.getAttribute('data-load-previous') === 'true';
    if (loadPrevious && currentPage > 1) {
      this.grid?.setAttribute('data-loading-previous', '');
      this.loadPreviousPages(currentPage).then(() => this.setupAutoLoad());
    } else {
      this.setupAutoLoad();
    }
  }

  setupAutoLoad() {
    if (this.autoLoad && this.container) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.loading) {
            this.loadMore();
          }
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this.container);
    }
  }

  async loadPreviousPages(targetPage) {
    this.loading = true;
    this.button?.classList.add('opacity-50', 'pointer-events-none');

    // Hide grid — reuse existing loading text if present
    this.grid.classList.add('opacity-0');

    const fragments = [];
    const url = new URL(window.location.href);

    // sequentially load previous pages to keep the same order of products
    for (let page = 1; page < targetPage; page++) {
      try {
        url.searchParams.set('page', page);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-product-grid]');
        if (newGrid) {
          fragments.push(newGrid.innerHTML);
        }
      } catch (error) {
        console.error(`Failed to load page ${page}:`, error);
      }
    }

    // Prepend previous pages' products before current page
    if (fragments.length > 0) {
      this.grid.insertAdjacentHTML('afterbegin', fragments.join(''));
    }

    // Wait for all images to load (with timeout fallback)
    const allImages = [...this.grid.querySelectorAll('img')];
    allImages.forEach((img) => img.setAttribute('loading', 'eager'));
    await Promise.all(
      allImages
        .filter((img) => !img.complete)
        .map((img) =>
          Promise.race([
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }),
            new Promise((resolve) => setTimeout(resolve, 10000)),
          ])
        )
    );

    // Reveal grid with all products in correct order
    this.grid.removeAttribute('data-loading-previous');
    const loadingEl = document.querySelector('[data-grid-loading]');
    if (loadingEl) loadingEl.remove();
    requestAnimationFrame(() => {
      this.grid.classList.remove('opacity-0', 'grid-reveal-fallback');
    });

    // Update progress
    const currentCount = this.grid.querySelectorAll(
      '[data-product-card]'
    ).length;
    this.updateProgress(currentCount);

    this.loading = false;
    this.button?.classList.remove('opacity-50', 'pointer-events-none');
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.controller.abort();
  }

  async loadMore() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;

    const fetchUrl = this.nextUrl;
    this.button?.classList.add('opacity-50', 'pointer-events-none');

    // Show loading text after the grid
    const loadingText = document.createElement('p');
    loadingText.className = 'text-body py-20 text-center';
    loadingText.setAttribute('data-grid-loading', '');
    loadingText.textContent =
      (this.getAttribute('data-loading-text') || 'Loading');
    this.grid.after(loadingText);

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Append new products hidden
      const newGrid = doc.querySelector('[data-product-grid]');
      const newCards = [];
      if (newGrid) {
        const template = document.createElement('template');
        template.innerHTML = newGrid.innerHTML;
        const nodes = [...template.content.children];
        nodes.forEach((node) => {
          node.style.opacity = '0';
          this.grid.appendChild(node);
          newCards.push(node);
        });
      }

      // Force eager loading so images load while hidden (with timeout fallback)
      const newImages = newCards.flatMap((card) => [
        ...card.querySelectorAll('img'),
      ]);
      newImages.forEach((img) => img.setAttribute('loading', 'eager'));
      await Promise.all(
        newImages
          .filter((img) => !img.complete)
          .map((img) =>
            Promise.race([
              new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              }),
              new Promise((resolve) => setTimeout(resolve, 10000)),
            ])
          )
      );

      // Reveal new products
      loadingText.remove();
      newCards.forEach((card) => {
        card.style.opacity = '';
      });

      // Check if there's another page
      const newLoadMore = doc.querySelector('load-more');
      const newNextUrl = newLoadMore?.getAttribute('data-next-url');

      if (newNextUrl) {
        this.nextUrl = newNextUrl;
      } else {
        this.nextUrl = null;
        this.container?.remove();
        if (this.observer) this.observer.disconnect();
      }

      // Update progress
      const currentCount = this.grid.querySelectorAll(
        '[data-product-card]'
      ).length;
      this.updateProgress(currentCount);

      // Update browser URL for SEO
      window.history.replaceState({}, '', fetchUrl);
    } catch (error) {
      console.error('Load more failed:', error);
    } finally {
      this.loading = false;
      this.button?.classList.remove('opacity-50', 'pointer-events-none');
    }
  }

  updateProgress(currentCount) {
    if (!this.totalProducts || this.totalProducts <= 0) return;

    const shown = Math.min(currentCount, this.totalProducts);
    const percent = Math.round((shown / this.totalProducts) * 100);

    if (this.progressCurrent) {
      this.progressCurrent.textContent = shown;
    }
    if (this.progressBar) {
      this.progressBar.style.width = `${percent}%`;
    }
  }
}

if (!customElements.get('load-more')) {
  customElements.define('load-more', LoadMore);
}
